// Nagłówek (B1): przyklejanie (kompaktowy pasek, spec 24), mega menu, panel „Kategorie”, szuflada menu,
// liczniki, panel wyszukiwania. Tryb i wysokości liczy skrypt w header.liquid (D17).
import { EVENTS, Theme, announce, emit, loadStyle, lockScroll, onIdle, reducedMotion, trapFocus, unlockScroll } from '@theme/core';

const SWISHLIST = '.st-wishlist-details-button[data-type="floating"]';
const visible = (el) => el.checkVisibility?.() ?? el.getClientRects().length > 0;

// Edytor: otwarta szuflada wraca po odświeżeniu podglądu (05 §5).
function editorDrawer(open) {
  try {
    if (open === undefined) return sessionStorage.getItem('header-drawer-open');
    open ? sessionStorage.setItem('header-drawer-open', '1') : sessionStorage.removeItem('header-drawer-open');
  } catch {}
}

class HeaderBar extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const on = (target, type, handler, options) => target?.addEventListener(type, handler, { signal: this.controller.signal, ...options });
    this.list = this.querySelector('[data-menu-list]');
    this.moreList = this.querySelector('[data-menu-more-list]');
    this.cartIcon = this.querySelector('[data-cart-icon]');
    this.toggle = this.querySelector('[data-drawer-toggle]');
    this.categories = this.querySelector('[data-categories-toggle]');
    this.panel = this.querySelector('.header__nav');

    this.observeSticky();
    on(this, 'click', (event) => this.onClick(event));
    on(this, EVENTS.headerChange, ({ detail }) => {
      this.closeCategories();
      detail?.mode === 'drawer' ? this.closeMenu() : this.drawer?.close({ focus: false });
      // Zmiana trybu (skrypt w header.liquid): inna zasada przyklejenia i inna wysokość paska.
      if (detail?.mode !== this.observedMode) this.observeSticky();
    });
    // preventDefault: ten Esc nie zamyka zadokowanej szuflady koszyka (cart.js).
    on(this, 'keydown', (event) => {
      if (event.key !== 'Escape' || !this.categoriesOpen) return;
      event.preventDefault();
      this.closeCategories(true);
    });
    // Fokus poza panelem i przyciskiem (też na akcje za panelem) zamyka panel.
    on(this, 'focusout', ({ relatedTarget: to }) => this.categoriesOpen && to && !this.panel.contains(to) && !this.categories.contains(to) && this.closeCategories());
    on(document, 'pointerdown', ({ target }) => this.categoriesOpen && !this.panel.contains(target) && !this.categories.contains(target) && this.closeCategories());
    on(this.list, 'pointerover', (event) => this.onMenuOver(event), { passive: true });
    on(this.list, 'pointerout', (event) => this.onMenuOut(event), { passive: true });
    on(this.list, 'pointermove', (event) => this.onPointerMove(event), { passive: true });
    on(this.list, 'pointerdown', (event) => (this.pointerType = event.pointerType), { passive: true });
    on(this.list, 'focusin', (event) => this.onMenuFocus(event));
    on(this.list, 'focusout', (event) => this.onMenuBlur(event));
    on(this.list, 'click', (event) => this.onTabClick(event));
    on(this.list, 'keydown', (event) => this.onMenuKeydown(event));

    on(document, EVENTS.cartChange, ({ detail }) => Number.isInteger(detail?.cart?.item_count) && this.renderCart(detail.cart.item_count, true));
    on(window, 'pageshow', (event) => event.persisted && this.restoreCart());
    on(window, 'pagehide', () => (this.closeCategories(), this.closeMenu(), this.drawer?.close({ focus: false, instant: true })));
    this.restoreCart();

    const searchBar = this.querySelector('.search--bar');
    const search = searchBar || document.getElementById('SearchPanel');
    for (const type of ['pointerenter', 'focusin']) on(searchBar, type, () => import('@theme/search'), { once: true });
    // Idle: wyszukiwarka i arkusz paska i panelu (desktop) — przewinięcie nie czeka na sieć.
    const idle = () => onIdle(() => (search && import('@theme/search'), this.menuMode && this.loadCompact()), 5000);
    document.readyState === 'complete' ? idle() : on(window, 'load', idle, { once: true });

    // Edytor: inna sekcja grupy zmienia --header-group-height; szuflada wraca po odświeżeniu.
    const relayout = ({ target }) => target.closest?.('.shopify-section-group-header-group') && !target.contains(this) && setTimeout(() => this.layout?.());
    on(document, 'shopify:section:load', relayout);
    on(document, 'shopify:section:unload', relayout);
    if (Theme.designMode && this.dataset.menuStyle === 'drawer' && editorDrawer()) this.openDrawer();
  }

  disconnectedCallback() {
    this.controller.abort();
    for (const observer of this.observers || []) observer.disconnect();
    this.closeMenu();
    for (const node of [this.sentinel, this.overlay, this.drawer]) node?.remove();
    this.overlay = this.drawer = null;
  }

  get menuMode() {
    return this.dataset.menuStyle !== 'drawer';
  }

  // Przyklejenie bez nasłuchu scroll — obserwatory krawędzi (FR-05-08, spec 24):
  // top — wartownik tuż nad sekcją minął górę okna (tryb „drawer”: sekcja sticky, jak dotąd);
  // bottom — dolna krawędź pełnego nagłówka minęła górę okna (tryb „menu”: wjazd kompaktowego paska);
  // back — ta krawędź jest wyżej niż wysokość paska (powrót do pełnego dopiero niżej — histereza).
  // root: document — rootMargin działa też w ramce edytora.
  observeSticky() {
    if (!this.hasAttribute('data-sticky')) return;
    const section = this.closest('.shopify-section') || this;
    if (!this.sentinel) {
      this.sentinel = Object.assign(document.createElement('div'), { className: 'header-sentinel' });
      this.sentinel.setAttribute('aria-hidden', 'true');
      section.before(this.sentinel);
    }
    for (const observer of this.observers || []) observer.disconnect();
    this.observedMode = this.dataset.menuStyle;
    this.edges = {};
    const meta = document.querySelector('meta[name="theme-color"]');
    const color = getComputedStyle(this).backgroundColor;
    const compact = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height-stuck')) || 0;
    const watch = (edge, target, offset) => {
      const observer = new IntersectionObserver(([entry]) => {
        this.edges[edge] = !entry.isIntersecting && entry.boundingClientRect.top < entry.rootBounds.top;
        this.updateStuck();
        meta?.setAttribute('content', color);
      }, { root: document, rootMargin: `${-offset}px 0px 0px` });
      observer.observe(target);
      return observer;
    };
    this.observers = [watch('top', this.sentinel, 0), watch('bottom', section, 0), watch('back', section, compact)];
  }

  // Wygląd paska w header-compact.css (nieblokujący): wczytanie w idle albo przy krawędzi `back`;
  // pasek wjeżdża dopiero z arkuszem — bez nieostylowanego stanu.
  updateStuck() {
    const { top, bottom, back } = this.edges;
    if (!this.menuMode) return this.setStuck(Boolean(top));
    if (back) this.loadCompact();
    if (bottom) this.compactReady && this.setStuck(true);
    else if (back === false) this.setStuck(false);
  }

  loadCompact() {
    this.compactCss ??= loadStyle(this.dataset.compactCss).then(() => {
      this.compactReady = true;
      if (this.edges) this.updateStuck();
    });
  }

  setStuck(stuck) {
    if (stuck === this.classList.contains('is-stuck')) return;
    const focused = this.contains(document.activeElement) && document.activeElement;
    // Panel ma geometrię liczoną dla poprzedniego stanu nagłówka.
    this.closeCategories();
    this.closeMenu();
    this.classList.toggle('is-stuck', stuck);
    // Fokus ze znikającego elementu na odpowiednik (nie <body>): przycisk ↔ przycisk, menu → przycisk paska.
    if (focused && !visible(focused)) {
      const toggle = [...this.querySelectorAll('[data-categories-toggle]')].find(visible);
      const link = (this.itemFrom(focused) || this.list?.firstElementChild)?.querySelector('.header__menu-link');
      this.restoringFocus = true; // bez otwierania mega menu fokusem
      ((stuck || focused.hasAttribute('data-categories-toggle')) && toggle || link)?.focus({ preventScroll: true });
      this.restoringFocus = false;
    }
    emit(EVENTS.headerChange, { stuck, mode: this.menuMode ? 'menu' : 'drawer' }, this);
  }

  onClick(event) {
    const target = event.target;
    if (target.closest('[data-drawer-toggle]')) return this.drawer?.classList.contains('is-open') ? this.drawer.close() : this.openDrawer();
    // Dwa przyciski (rząd menu, pasek); klik z klawiatury (detail 0) przenosi fokus do panelu.
    const toggle = target.closest('[data-categories-toggle]');
    if (toggle) {
      if (this.categoriesOpen) return this.closeCategories();
      this.categories = toggle;
      return this.openCategories(event.detail === 0);
    }
    // Klik koszyka [data-cart-trigger] i jego aria-expanded obsługuje cart.js (H).
    if (target.closest('[data-search-open]')) this.openSearch();
  }

  // Panel wyszukiwania (B2): synchronicznie showPopover + fokus, potem moduł (ARCHITEKTURA §17.2).
  openSearch(text = '') {
    const panel = document.getElementById('SearchPanel');
    const input = panel?.querySelector('[data-search-input]');
    if (!input) return false;
    this.drawer?.close({ focus: false });
    if (text) input.value = text;
    try {
      panel.showPopover();
    } catch {} // już otwarty albo brak Popover API przed polyfillem
    input.focus({ preventScroll: true });
    import('@theme/search');
    return true;
  }

  // ===== Panel „Kategorie” =====
  // To samo menu, w CSS (header-compact.css — pierwsze otwarcie czeka na arkusz) lista | mega menu.

  openCategories(focus) {
    if (!this.panel) return;
    if (!this.compactReady) return this.loadCompact(), this.compactCss.then(() => this.openCategories(focus));
    this.categoriesOpen = true;
    this.classList.add('is-categories-open');
    this.categories.setAttribute('aria-expanded', 'true');
    this.unlockImages();
    const items = [...this.list.children].filter((item) => !item.hidden);
    const first = items.find((item) => item.querySelector(':scope > .mega'));
    if (first) this.openMenu(first, true);
    else this.ensureOverlay().classList.add('is-visible');
    if (focus) items[0]?.querySelector('.header__menu-link').focus({ preventScroll: true });
  }

  closeCategories(restoreFocus = false) {
    if (!this.categoriesOpen) return;
    this.categoriesOpen = false;
    clearTimeout(this.hoverTimer);
    this.hoverItem = null;
    this.classList.remove('is-categories-open');
    this.categories.setAttribute('aria-expanded', 'false');
    this.closeMenu();
    this.overlay?.classList.remove('is-visible');
    if (restoreFocus) this.categories.focus();
  }

  // Pozycja listy aktywuje się po 70 ms spoczynku kursora — ukośny zjazd do treści nie przełącza.
  hoverCategory(event, item) {
    const next = event.target.closest('.header__menu-link') ? item : this.openItem;
    if (next === this.hoverItem) return; // już czeka
    clearTimeout(this.hoverTimer);
    this.hoverItem = null;
    if (!next || next === this.openItem) return;
    this.hoverItem = next;
    const later = () => (this.hoverTimer = setTimeout(() => {
      if (this.aiming(this.openItem?.querySelector(':scope > .mega'))) return later();
      this.hoverItem = null;
      this.openMenu(next);
    }, 70));
    later();
  }

  // ===== Mega menu =====

  itemFrom(node) {
    for (let el = node instanceof Element ? node : null; el && el !== this.list; el = el.parentElement) {
      if (el.parentElement === this.list) return el;
    }
    return null;
  }

  onMenuOver(event) {
    if (!this.menuMode) return;
    this.unlockImages();
    if (event.pointerType === 'touch') return;
    const item = this.itemFrom(event.target);
    if (this.categoriesOpen) this.hoverCategory(event, item);
    else if (item) this.openMenu(item);
    this.onPanelOver(event);
  }

  onMenuOut(event) {
    const { target, relatedTarget } = event;
    const preview = target.closest?.('[data-mega-preview]');
    if (preview && !preview.contains(relatedTarget)) this.hidePreview(preview);
    const tab = target.closest?.('[data-mega-tab]');
    if (tab && !tab.contains(relatedTarget)) clearTimeout(this.tabTimer);
    // Przejście na inną pozycję albo w odstęp między nimi przełączy pointerover; `contains` chroni przed
    // fałszywym pointerleave przy przebudowie warstw. Panel „Kategorie” zamyka tylko Esc, klik poza, przycisk.
    if (this.categoriesOpen || !this.openItem || this.openItem.contains(relatedTarget) || relatedTarget === this.list || this.itemFrom(relatedTarget)) return;
    this.closeMenu();
  }

  onMenuFocus(event) {
    if (!this.menuMode || this.restoringFocus) return;
    this.unlockImages();
    const item = this.itemFrom(event.target);
    if (item) this.openMenu(item, true);
    const preview = event.target.closest('[data-mega-preview]');
    if (preview) this.showPreview(preview);
    const tab = event.target.closest('[data-mega-tab]');
    if (tab) this.selectTab(tab);
    const sub = event.target.closest('[data-menu-item]');
    if (sub?.parentElement === this.moreList) this.openMoreItem(sub);
  }

  onMenuBlur(event) {
    const preview = event.target.closest('[data-mega-preview]');
    if (preview) this.hidePreview(preview);
    const item = this.openItem;
    if (this.categoriesOpen || !item || item.contains(event.relatedTarget) || this.itemFrom(event.relatedTarget) || item.matches(':hover')) return;
    this.closeMenu();
  }

  openMenu(item, viaFocus = false) {
    const current = this.openItem;
    if (item === current) return;
    const panel = item.querySelector(':scope > .mega');
    // W panelu „Kategorie” pozycja bez podmenu zostawia treść poprzedniej (to tylko link).
    if (!panel) return this.categoriesOpen || this.closeMenu();
    // Odczyt przed zapisami: nowy panel rośnie od wysokości poprzedniego (bez mignięcia przy przełączaniu).
    const from = current?.querySelector(':scope > .mega')?.offsetHeight || 0;
    if (current) this.setOpen(current, false);
    panel.style.setProperty('--mega-from', `${from}px`);
    this.openItem = item;
    this.setOpen(item, true);
    this.list.classList.add('is-open');
    this.classList.add('is-menu-open');
    this.ensureOverlay().classList.add('is-visible');
    // Każde otwarcie zaczyna od pierwszej kategorii albo „Wszystkie” (FR-05-12).
    const firstTab = panel.querySelector('[data-mega-tab]');
    if (firstTab) this.selectTab(firstTab);
    if (item.hasAttribute('data-menu-more')) this.openMoreItem(this.moreList.firstElementChild);
    // Po 150 ms najechania (od razu przy fokusie) zamyka pływający czat Inbox (FR-05-12, GL-CHT-01).
    clearTimeout(this.chatTimer);
    this.chatTimer = setTimeout(() => {
      const chat = document.querySelector('shopify-chat[open]');
      if (chat && chat.getAttribute('mode') !== 'managed') chat.removeAttribute('open');
    }, viaFocus ? 0 : 150);
  }

  closeMenu(restoreFocus = false) {
    const item = this.openItem;
    if (!item) return;
    this.openItem = null;
    this.setOpen(item, false);
    this.list.classList.remove('is-open');
    this.classList.remove('is-menu-open');
    this.overlay?.classList.remove('is-visible');
    item.querySelector(':scope > .header__menu-link')?.classList.remove('is-guarded');
    for (const timer of [this.chatTimer, this.tabTimer, this.guardTimer]) clearTimeout(timer);
    if (!restoreFocus) return;
    this.restoringFocus = true;
    item.querySelector(':scope > .header__menu-link')?.focus();
    this.restoringFocus = false;
  }

  setOpen(item, open) {
    item.classList.toggle('is-open', open);
    item.querySelector(':scope > .header__menu-link')?.setAttribute('aria-expanded', open);
    // Produkty kolekcji (także w „Więcej”): moduł i karty dopiero przy pierwszym otwarciu panelu.
    const products = open && item.querySelector(':scope > [data-mega-products]:not([data-state])');
    if (products) import('@theme/header-products').then((module) => module.load(products));
  }

  openMoreItem(item) {
    if (!item || item.classList.contains('is-open')) return;
    for (const other of this.moreList.children) this.setOpen(other, other === item);
  }

  // Nakładka rozmycia w <body>, poza stosem nagłówka: pasek ogłoszeń (12) zostaje nad nią.
  ensureOverlay() {
    if (!this.overlay) {
      this.overlay = Object.assign(document.createElement('div'), { className: 'header-overlay' });
      this.overlay.addEventListener('click', () => this.closeMenu());
      document.body.append(this.overlay);
    }
    return this.overlay;
  }

  // Tarcza: dopóki kursor się rusza, pas pod pozycją należy do niej; po 50 ms bezruchu znika, a pozycja
  // pod kursorem jest sprawdzana ponownie (Safari nie wysyła wtedy zdarzeń).
  onPointerMove(event) {
    if (event.timeStamp - (this.aimFrom?.t ?? -1e9) > 40) this.aimFrom = this.aimTo;
    this.aimTo = { x: event.clientX, y: event.clientY, t: event.timeStamp };
    const link = this.openItem?.querySelector(':scope > .header__menu-link');
    if (!link || this.categoriesOpen) return;
    this.pointer = [event.clientX, event.clientY];
    // Jedna tarcza naraz: tarcza poprzedniej pozycji przejmowałaby zjazd do panelu nowej.
    if (this.guarded !== link) this.guarded?.classList.remove('is-guarded');
    this.guarded = link;
    if (!link.classList.contains('is-guarded')) link.classList.add('is-guarded');
    clearTimeout(this.guardTimer);
    this.guardTimer = setTimeout(() => {
      link.classList.remove('is-guarded');
      requestAnimationFrame(() => {
        const item = this.openItem && this.itemFrom(document.elementFromPoint(...this.pointer));
        if (item && item !== this.openItem) this.openMenu(item);
      });
    }, 50);
  }

  // Obrazy menu są leniwe; przy pierwszej intencji w trybie menu pobierają się wszystkie (FR-05-18).
  unlockImages() {
    if (this.imagesUnlocked) return;
    this.imagesUnlocked = true;
    for (const image of this.list.querySelectorAll('img[loading="lazy"]')) image.removeAttribute('loading');
  }

  // Celowanie w treść: kursor jedzie w prawo, w stronę otwartego panelu, więc pozycje mijane po drodze
  // się nie przełączają (sprawdzane ponownie, aż kursor stanie albo zmieni kierunek).
  aiming(panel) {
    const [a, b, box] = [this.aimFrom, this.aimTo, panel?.getBoundingClientRect()];
    if (!a || !b || !box || performance.now() - b.t > 120 || b.x <= a.x || a.x >= box.left) return false;
    const slope = (b.y - a.y) / (b.x - a.x);
    return slope > (box.top - a.y) / (box.left - a.x) && slope < (box.bottom - a.y) / (box.left - a.x);
  }

  // Zakładki i litery: najechanie po 120 ms, fokus od razu; dotyk — 1. stuknięcie przełącza, 2. otwiera link.
  onPanelOver(event) {
    const preview = event.target.closest('[data-mega-preview]');
    if (preview) this.showPreview(preview);
    const sub = event.target.closest('[data-menu-item]');
    if (sub?.parentElement === this.moreList) this.openMoreItem(sub);
    const tab = event.target.closest('[data-mega-tab]');
    if (!tab || tab.contains(event.relatedTarget)) return;
    clearTimeout(this.tabTimer);
    if (tab.classList.contains('is-active')) return;
    const panels = tab.closest('[data-mega-tabs]')?.querySelector('[data-mega-panel]')?.parentElement;
    const later = () => (this.tabTimer = setTimeout(() => (this.aiming(panels) ? later() : this.selectTab(tab)), 120));
    later();
  }

  onTabClick(event) {
    const tab = event.target.closest('[data-mega-tab]');
    if (!tab || tab.classList.contains('is-active')) return;
    if (tab.href && this.pointerType === 'mouse') return;
    event.preventDefault();
    clearTimeout(this.tabTimer);
    this.selectTab(tab);
  }

  // Nieaktywny panel od razu `inert`: w trakcie wygaszania nie przejmie fokusu (inaczej fokus spada na BODY).
  selectTab(tab) {
    const root = tab.closest('[data-mega-tabs]');
    for (const other of root.querySelectorAll('[data-mega-tab]')) {
      other.classList.toggle('is-active', other === tab);
      other.setAttribute(other.hasAttribute('aria-pressed') ? 'aria-pressed' : 'aria-expanded', other === tab);
    }
    for (const panel of root.querySelectorAll('[data-mega-panel]')) {
      const active = panel.dataset.megaPanel === tab.dataset.megaTab;
      panel.classList.toggle('is-active', active);
      panel.inert = !active;
    }
  }

  // Escape zamyka panel. Tab: aktywna zakładka/litera → jej panel → następna zakładka; Shift+Tab
  // z początku panelu wraca do zakładki. Panele leżą w DOM za całą listą zakładek, więc kolejność
  // ustala skrypt, bez zmiany wyglądu.
  onMenuKeydown(event) {
    if (event.key === 'Escape') {
      if (this.categoriesOpen || !this.openItem) return;
      event.preventDefault();
      return this.closeMenu(true);
    }
    const root = event.target.closest?.('[data-mega-tabs]');
    if (event.key !== 'Tab' || !root || !this.menuMode) return;
    const tabs = [...root.querySelectorAll('[data-mega-tablist] :is(a, button)')];
    const active = root.querySelector('[data-mega-tab].is-active');
    const panel = root.querySelector('[data-mega-panel].is-active');
    const links = panel ? [...panel.querySelectorAll('a, button')].filter((el) => el.tabIndex >= 0) : [];
    let next = null;
    if (event.shiftKey) {
      if (event.target === links[0]) next = active;
    } else if (event.target === active) {
      next = links[0];
    } else if (event.target === links.at(-1)) {
      next = tabs[tabs.indexOf(active) + 1];
    } else if (event.target === tabs.at(-1) && panel) {
      // Ostatnia zakładka bez własnego panelu: Tab omija panel innej zakładki i wychodzi z mega menu.
      panel.inert = true;
      setTimeout(() => (panel.inert = !panel.classList.contains('is-active')));
    }
    if (!next) return;
    event.preventDefault();
    next.focus();
  }

  // Zdjęcie kolekcji nad zdjęciem kategorii; pobierane dopiero przy najechaniu, ostatnie wygrywa.
  showPreview(link) {
    const media = link.closest('.mega__panel')?.querySelector('.mega-tabs__media');
    const src = link.dataset.megaPreview;
    if (!media) return;
    media.dataset.previewFor = src;
    const image = new Image();
    image.src = src;
    image.decode().then(() => {
      if (media.dataset.previewFor !== src) return;
      let preview = media.querySelector('.mega-tabs__preview');
      if (!preview) {
        preview = Object.assign(document.createElement('span'), { className: 'mega-tabs__preview' });
        preview.append(Object.assign(document.createElement('img'), { alt: '' }));
        media.append(preview);
        void preview.offsetWidth; // pierwsze pokazanie też z przejściem
      }
      preview.firstChild.src = src;
      preview.classList.add('is-visible');
    }, () => {});
  }

  hidePreview(link) {
    const media = link.closest('.mega__panel')?.querySelector('.mega-tabs__media');
    if (!media) return;
    delete media.dataset.previewFor;
    media.querySelector('.mega-tabs__preview')?.classList.remove('is-visible');
  }

  // ===== Szuflada menu =====

  // Szablon z menu-data (D13) klonowany przy pierwszym otwarciu; region tylko z szablonu nagłówka.
  openDrawer() {
    if (!this.drawer) {
      const template = document.getElementById('MenuDrawerTemplateHeader') || document.getElementById('MenuDrawerTemplate');
      if (!template) return;
      const fragment = document.importNode(template.content, true);
      const region = document.getElementById('MenuDrawerLocalizationTemplate');
      if (region) {
        const parts = document.importNode(region.content, true);
        fragment.querySelector('.menu-drawer__scroll')?.append(parts.querySelector('.menu-drawer__localization'));
        fragment.querySelector('[data-drawer-panel]')?.append(parts.querySelector('[data-drawer-submenu]'));
      }
      this.drawer = fragment.querySelector('menu-drawer');
      this.drawer.header = this;
      document.body.append(fragment);
      // Dopiero teraz #MenuDrawer istnieje — wcześniej aria-controls wskazywałoby pusty identyfikator.
      this.toggle?.setAttribute('aria-controls', this.drawer.id);
    }
    this.drawer.open(this.toggle);
  }

  // ===== Koszyk =====

  // 0 = ukryty, 1–99 = liczba, ≥100 = kropka; pełna liczba dla czytników (FR-05-26, BG-25). Ogłoszenie
  // przez #LiveRegion — „Dodano” z formularza zaraz je zastępuje, więc czytnik słyszy jeden komunikat.
  renderCart(count, notify) {
    const icon = this.cartIcon;
    if (!icon) return;
    const bubble = icon.querySelector('.header__bubble');
    const counter = this.querySelector('[data-cart-count]');
    const changed = Number(counter.textContent) !== count;
    icon.classList.toggle('has-count', count > 0);
    icon.classList.toggle('is-dot', count > 99);
    bubble.hidden = count === 0;
    bubble.querySelector('[data-bubble-text]').textContent = count > 0 && count < 100 ? count : '';
    counter.textContent = count;
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: count, timestamp: Date.now() }));
    } catch {}
    if (!notify || !changed) return;
    announce(`${Theme.strings?.cartCount}: ${count}`);
    if (count === 0 || reducedMotion()) return;
    bubble.classList.add('is-animating');
    bubble.addEventListener('animationend', () => bubble.classList.remove('is-animating'), { once: true });
  }

  // Powrót z bfcache lub start: zapis młodszy niż 10 s i inny niż widoczny poprawia licznik bez animacji.
  restoreCart() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem('cart-count'));
    } catch {}
    const shown = Number(this.querySelector('[data-cart-count]')?.textContent);
    if (saved && Date.now() - saved.timestamp < 10000 && Number(saved.value) !== shown) this.renderCart(Number(saved.value), false);
  }
}

class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('click', (event) => this.onClick(event), options);
    this.addEventListener('keydown', (event) => this.onKeydown(event), options);
    // Pole szukania otwiera panel wyszukiwarki (B2) z wpisanym tekstem. Stuknięcie nie daje fokusu (bez
    // klawiatury pod szufladą); z klawiatury pole przejmuje panel przy pierwszym znaku, nie przy samym Tab.
    const search = this.querySelector('[data-drawer-search]');
    if (!search || !document.getElementById('SearchPanel')) return;
    search.addEventListener('pointerdown', (event) => event.preventDefault(), options);
    search.addEventListener('click', () => this.header?.openSearch(search.value), options);
    search.addEventListener('input', () => this.header?.openSearch(search.value), options);
  }

  disconnectedCallback() {
    this.controller.abort();
    this.release?.();
    clearTimeout(this.timer);
    unlockScroll(this);
  }

  open(trigger) {
    if (this.classList.contains('is-open')) return;
    clearTimeout(this.timer);
    this.trigger = trigger;
    this.hidden = false;
    lockScroll(this);
    void this.panel.offsetWidth; // stan początkowy przed klasą — inaczej bez przejścia
    this.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    this.panel.focus({ preventScroll: true });
    this.release = trapFocus(this.panel);
    if (Theme.designMode) editorDrawer(true);
  }

  close({ focus = true, instant = false } = {}) {
    if (!this.classList.contains('is-open')) return;
    const hadFocus = this.contains(document.activeElement);
    this.classList.remove('is-open');
    this.release?.();
    this.release = null;
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (focus && hadFocus) this.trigger?.focus({ preventScroll: true });
    if (Theme.designMode) editorDrawer(false);
    this.timer = setTimeout(() => {
      this.hidden = true;
      this.reset();
      unlockScroll(this);
    }, instant || reducedMotion() ? 0 : 200);
  }

  // Po zamknięciu wszystko wraca do 1. poziomu.
  reset() {
    for (const panel of this.querySelectorAll('[data-drawer-submenu].is-open')) this.closeSubmenu(panel, false);
    for (const details of this.querySelectorAll('details[open]')) details.open = false;
    this.querySelector('.menu-drawer__scroll').scrollTop = 0;
  }

  onClick(event) {
    const target = event.target;
    if (target.closest('[data-drawer-close]')) return this.close();
    const back = target.closest('[data-drawer-back]');
    if (back) return this.closeSubmenu(back.closest('[data-drawer-submenu]'));
    const opener = target.closest('[data-drawer-open-panel]');
    if (opener) this.openSubmenu(opener);
  }

  // Escape zamyka bieżący poziom: akordeon, podpanel, szufladę.
  onKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    const details = event.target.closest?.('details[open]');
    if (details) {
      details.open = false;
      return details.querySelector('summary').focus();
    }
    const panel = this.querySelector('[data-drawer-submenu].is-open');
    panel ? this.closeSubmenu(panel) : this.close();
  }

  openSubmenu(opener) {
    const panel = this.querySelector(`#${CSS.escape(opener.getAttribute('aria-controls'))}`);
    if (!panel) return;
    panel.opener = opener;
    panel.scrollTop = 0;
    panel.classList.add('is-open');
    opener.setAttribute('aria-expanded', 'true');
    panel.querySelector('[data-drawer-back]')?.focus({ preventScroll: true });
  }

  closeSubmenu(panel, focus = true) {
    panel.classList.remove('is-open');
    panel.opener?.setAttribute('aria-expanded', 'false');
    if (focus) panel.opener?.focus({ preventScroll: true });
  }
}

// Serce Swishlist: klik przekazywany do ukrytego pływającego przycisku aplikacji i jej licznik (FR-05-25).
// Aplikacja wstrzykuje przycisk do <body> z opóźnieniem i podmienia go po pierwszej interakcji.
class HeaderWishlist extends HTMLElement {
  connectedCallback() {
    this.icon = this.querySelector('[data-wishlist-icon]');
    this.controller = new AbortController();
    this.querySelector('button')?.addEventListener('click', () => this.open(), { signal: this.controller.signal });
    // Tylko dzieci <body> — tanio przez całe życie strony.
    this.bodyObserver = new MutationObserver(() => this.attach());
    this.bodyObserver.observe(document.body, { childList: true });
    this.attach();
  }

  disconnectedCallback() {
    this.controller.abort();
    this.bodyObserver.disconnect();
    this.countObserver?.disconnect();
    this.attached = null;
  }

  attach() {
    const floating = document.querySelector(SWISHLIST);
    if (!floating || floating === this.attached) return;
    this.attached = floating;
    this.countObserver?.disconnect();
    this.countObserver = new MutationObserver(() => this.sync());
    this.countObserver.observe(floating, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['data-count', 'class'] });
    this.sync();
    // Klik sprzed gotowości aplikacji też otwiera jej szufladę.
    if (this.pending) {
      this.pending = false;
      floating.click();
    }
  }

  sync() {
    const number = this.attached?.querySelector('.st-js-number');
    const count = Number(number?.getAttribute('data-count') ?? number?.textContent ?? 0) || 0;
    this.icon.classList.toggle('has-count', count > 0);
    this.icon.querySelector('.header__bubble').hidden = count === 0;
    this.icon.querySelector('[data-bubble-text]').textContent = count > 99 ? '99+' : count;
    this.querySelector('[data-wishlist-count]').textContent = count || '';
  }

  open() {
    const floating = document.querySelector(SWISHLIST);
    if (floating) floating.click();
    else this.pending = true;
  }
}

for (const [name, element] of [['header-bar', HeaderBar], ['menu-drawer', MenuDrawer], ['header-wishlist', HeaderWishlist]]) {
  if (!customElements.get(name)) customElements.define(name, element);
}
